<template>
    <NuxtLink :id="`r-id-${data?.slug || data?.id}`" :to="computedRoute" class="CMasonryCard" :class="{ 'CMasonryCard--no-image': hideImage, 'CMasonryCard--compact-image': compactImage }" @click="handleCardClick">
        <div v-if="!hideImage" class="image-container">
            <img
                class="image bg-gray-300 dark:bg-subtle"
                :class="{ 'image--contain': computedImageFit === 'contain' }"
                :src="resizedImageSrc(computedImageLink, CARD_IMAGE_WIDTH)"
                :srcset="resizedImageSrcset(computedImageLink, CARD_IMAGE_WIDTH)"
                :alt="computedTitle"
                :width="CARD_IMAGE_WIDTH"
                :height="CARD_IMAGE_HEIGHT"
                :loading="imageLoading"
            />
            <div class="toasts">
                <CBadge v-for="(toast, index) in computedToasts" :key="index" :type="computedToastTypes[index]" :title="computedToastTitles[index]">{{ toast }}</CBadge>
            </div>
        </div>
        <!-- When the image is hidden, surface the badges at the top of the card body instead. -->
        <div v-else-if="computedToasts.length" class="toasts-inline">
            <CBadge v-for="(toast, index) in computedToasts" :key="index" :type="computedToastTypes[index]" :title="computedToastTitles[index]">{{ toast }}</CBadge>
        </div>
        <h3 class="title">{{ computedTitle }}</h3>
        <!-- Description (bio/about) - shown before job like on homepage -->
        <p v-if="computedDescription" class="description">{{ computedDescription }}</p>
        <!-- Job/position with icon - like homepage mentor card -->
        <p v-if="computedSubscript" :class="showSubscriptIcon ? 'subscript-with-icon' : 'subscript'">
            <CIcon v-if="showSubscriptIcon" name="jobs" class="size-4 text-gray-400 dark:text-txt-body shrink-0" />
            <span>{{ computedSubscript }}</span>
        </p>
        <!-- Location and other attributes -->
        <div v-if="computedAttributes.length" class="attributes">
            <div class="attribute" v-for="(attribute, index) in computedAttributes" :key="index">
                <CIcon v-if="computedAttributeIcons[index]" :name="computedAttributeIcons[index]" class="size-4 text-gray-400 dark:text-txt-body shrink-0" />
                <span>{{ attribute }}</span>
            </div>
        </div>
        <!-- Tags with hashtag style like homepage -->
        <div v-if="computedTags.length" class="tagArea line-clamp-2">
            <span
                v-for="tag in computedTags.slice(0, 5)"
                :key="tag"
                class="tag-item font-semibold text-sm text-[#1d191f] dark:text-white leading-5 inline cursor-pointer"
                @click.prevent="handleTagClick(tag)"
            >
                <span class="text-[#047274] dark:text-primary-400">#</span>{{ tag }}&nbsp;&nbsp;
            </span>
        </div>
    </NuxtLink>
</template>

<script setup lang="ts">
import CBadge from './CBadge.vue'
import CIcon from './CIcon.vue'
import type { Job } from '#shared/types/Job'
import type { Project } from '#shared/types/Project'
import type { Org } from '#shared/types/Org'
import type { UserProfile } from '#shared/types/User'
import { JobPlaceType, JobType, FormOfCollaborationType } from '#shared/types/Job'
import { formOfCollaborationDictionary } from '#shared/types/education'
import { orgTypeDictionary } from '#shared/types/dictionary'
import type { OrgType } from '#shared/types/Org'
import { getFullName } from '#shared/utils'
import type { RouteLocationRaw } from 'vue-router'

// Helper to parse dates from various formats (Firestore Timestamp, ISO string, Date object)
const parseDate = (date: any): Date | null => {
    if (!date) return null
    // Firestore Timestamp object (has seconds property)
    if (date.seconds !== undefined) {
        return new Date(date.seconds * 1000)
    }
    // Already a Date object
    if (date instanceof Date) {
        return date
    }
    // ISO string or timestamp number
    const parsed = new Date(date)
    return isNaN(parsed.getTime()) ? null : parsed
}

// Helper to translate formOfCollaboration values to human-readable labels
const translateFormOfCollaboration = (values: string[]): string[] => {
    return values.map(value => {
        return formOfCollaborationDictionary[value as FormOfCollaborationType] || value
    })
}
import orderBy from 'lodash-es/orderBy'
import defaultImageUrl from '@/assets/images/user-profile-placeholder.svg'
import defaultOrgImageUrl from '@/assets/images/org-profile-placeholder.svg'
import { resizedImageSrc, resizedImageSrcset } from '@/utils/resizedImage'

// Union type for all supported data objects
type SupportedData = Job | Project | Org | UserProfile
type JobWithOrgPreview = Job & { orgImage?: string; orgName?: string }
type ProjectWithOrgPreview = Project & { orgImage?: string; orgName?: string }

interface Props {
    // Legacy props (for backward compatibility)
    imageLink?: string
    toasts?: string[]
    toastType?: string[]
    toastTitles?: string[]
    title?: string
    subscript?: string
    description?: string
    attributes?: string[]
    attributeIconNames?: string[]
    tags?: string[]
    page?: string
    pageParams?: Record<string, any>
    // Explicit route override (supports query, etc.) — wins over page/pageParams and data
    to?: RouteLocationRaw
    thumbnailType?: 'project' | 'job' | 'skill' | 'org' | 'profile' | 'volunteer' | 'mentor' | 'talentbase' | 'talentbase_anonymous' | 'page'
    imageFit?: 'cover' | 'contain'
    // Render a compact, image-less card (e.g. the photo-less TalentBase listing).
    hideImage?: boolean
    // The image is only a logo: on phones show it in a short band instead of a tall image box.
    compactImage?: boolean

    // New props for typed objects
    data?: SupportedData
    type?: 'job' | 'project' | 'org' | 'user' | 'mentor' | 'mentee' | 'volunteer'
}

const props = withDefaults(defineProps<Props>(), {
    imageLink: "",
    toasts: () => [],
    toastType: () => [],
    toastTitles: () => [],
    title: "",
    subscript: "",
    description: "",
    attributes: () => [],
    attributeIconNames: () => [],
    tags: () => [],
    page: "",
    pageParams: () => ({}),
    to: undefined,
    thumbnailType: undefined,
    imageFit: 'cover',
    hideImage: false,
    compactImage: false,
    data: undefined,
    type: undefined
})

// Listings render 20+ cards but only the first few are in the first viewport (on desktop /praca the
// first card image is the LCP element), so those load eagerly and the rest lazily. Cards are counted
// per page in render order, which is the same on the server and during hydration.
const EAGER_CARD_IMAGES = 4
const nuxtApp = useNuxtApp() as ReturnType<typeof useNuxtApp> & { _cardImageCount?: { path: string; count: number } }
const route = useRoute()
let cardImageCount = nuxtApp._cardImageCount
if (!cardImageCount || cardImageCount.path !== route.path) {
    cardImageCount = nuxtApp._cardImageCount = { path: route.path, count: 0 }
}
cardImageCount.count += 1
const imageLoading = cardImageCount.count <= EAGER_CARD_IMAGES ? 'eager' : 'lazy'

// Firebase Storage images are resized via ImageKit. Cards are ~280-400px wide in multi-column grids,
// up to ~600px on phones (DPR 2+, so they take the 2x). The image box is 1 / 0.8. Width only, so the
// ratio is kept and `contain` logos aren't cropped.
const CARD_IMAGE_WIDTH = 400
const CARD_IMAGE_HEIGHT = 320

// console.log('CMasonryCard instantiated with data:', props.data)

const emit = defineEmits<{
    (e: 'tag-click', tag: string): void
    (e: 'click', event: MouseEvent): void
}>()

// Type guards
const isJob = (data: any): data is Job => {
    return data && ('jobPlace' in data || 'formOfCollaboration' in data || 'orgMeta' in data)
}

const isProject = (data: any): data is Project => {
    return data && ('how' in data || 'maturity' in data || 'orgId' in data) && !('firstName' in data || 'lastName' in data || 'mentorProfile' in data)
}

const isOrg = (data: any): data is Org => {
    return data && ('memberIds' in data || 'type' in data || 'ico' in data)
}

const isUser = (data: any): data is UserProfile => {
    return data && ('firstName' in data || 'lastName' in data || 'geoCity' in data || 'interests' in data || 'mentorProfile' in data || 'id' in data)
}

// Processing functions for different data types
const processJobData = (job: Job) => {
    const jobPreview = job as JobWithOrgPreview
    const toasts: string[] = []
    const toastTypes: string[] = []
    const toastTitles: string[] = []

    // Parse endDate from various formats (Firestore Timestamp, ISO string, etc.)
    const endDate = parseDate(job.endDate)
    if (endDate) {
        toasts.push(endDate.toLocaleDateString('sk-SK'))
        toastTypes.push('default')
        toastTitles.push('Deadline na prihlásenie')
    }

    if (job.jobPlace?.includes(JobPlaceType.hybrid) || job.jobPlace?.includes(JobPlaceType.homeoffice)) {
        toasts.push('ONLINE')
        toastTypes.push('success')
        toastTitles.push('')
    }

    const attributes: string[] = []
    const attributeIcons: string[] = []

    if (job.formOfCollaboration?.length) {
        // Translate technical values to human-readable labels
        const translatedValues = translateFormOfCollaboration(job.formOfCollaboration)
        attributes.push(translatedValues.join(', '))
        attributeIcons.push('work-type')
    }

    if (job.location) {
        attributes.push(job.location)
        attributeIcons.push('location-outline')
    }

    return {
        imageLink: job.image || jobPreview.orgImage || job.orgMeta?.image || '',
        title: job.title || '',
        subscript: jobPreview.orgName || job.orgMeta?.title || '',
        toasts,
        toastTypes,
        toastTitles,
        attributes: attributes.filter(Boolean),
        attributeIcons: attributeIcons.filter(Boolean),
        tags: job.topics || [],
        route: { name: 'praca-slug', params: { slug: job.slug || job.id } }
    }
}

const processProjectData = (project: Project) => {
    const projectPreview = project as ProjectWithOrgPreview
    const toasts: string[] = []
    const toastTypes: string[] = []

    // Parse unpublishAt from various formats (Firestore Timestamp, ISO string, etc.)
    const unpublishAt = parseDate(project.unpublishAt)
    if (unpublishAt) {
        toasts.push(unpublishAt.toLocaleDateString('sk-SK'))
        toastTypes.push('default')
    }

    const attributes: string[] = []
    const attributeIcons: string[] = []

    if (project.duration?.length) {
        attributes.push(project.duration.join(', '))
        attributeIcons.push('duration')
    }

    if (project.geoCity) {
        attributes.push(project.geoCity)
        attributeIcons.push('location-outline')
    }

    if (project.when) {
        attributes.push(project.when)
        attributeIcons.push('calendar')
    }

    return {
        imageLink: project.image || projectPreview.orgImage || project.orgMeta?.image || '',
        title: project.title || '',
        subscript: projectPreview.orgName || project.orgMeta?.title || '',
        toasts,
        toastTypes,
        attributes: attributes.filter(Boolean),
        attributeIcons: attributeIcons.filter(Boolean),
        tags: project.topics || [],
        route: { name: 'dobrovolnictvo-dobrovolnicke-projekty-id', params: { id: project.slug || project.id } }
    }
}

// Slovak noun forms for 1 / 2–4 / 0 and 5+
const pluralize = (count: number, [one, few, many]: [string, string, string]) => {
    const form = count === 1 ? one : count >= 2 && count <= 4 ? few : many
    return `${count} ${form}`
}

// "2 ponuky · 1 projekt" from the org's active opportunity counters (kept on the org document
// and returned in search hits, though not indexed). Empty when nothing is open.
const formatOrgOpportunities = (org: any): string => {
    const jobs = Number(org.opportunities?.jobs?.active) || 0
    const projects = Number(org.opportunities?.projects?.active) || 0
    return [
        jobs > 0 ? pluralize(jobs, ['ponuka', 'ponuky', 'ponúk']) : '',
        projects > 0 ? pluralize(projects, ['projekt', 'projekty', 'projektov']) : ''
    ]
        .filter(Boolean)
        .join(' · ')
}

const processOrgData = (org: any) => {
    const attributes: string[] = []
    const attributeIcons: string[] = []

    const opportunities = formatOrgOpportunities(org)
    if (opportunities) {
        attributes.push(opportunities)
        attributeIcons.push('jobs')
    }

    // Handle both Typesense (geoCity) and Firestore (geocity) field names
    const location = org.geocity || org.geoCity
    if (location) {
        attributes.push(location)
        attributeIcons.push('location-outline')
    }

    // Translate org type to human-readable label
    const orgTypeLabel = org.type ? (orgTypeDictionary[org.type as OrgType] || org.type) : ''

    // Handle both Typesense (name, logo) and Firestore (title, image) field names
    const title = org.title || org.name || ''
    const image = org.image || org.logo || defaultOrgImageUrl

    return {
        imageLink: image,
        title: title,
        subscript: orgTypeLabel,
        toasts: [],
        toastTypes: [],
        attributes: attributes.filter(Boolean),
        attributeIcons: attributeIcons.filter(Boolean),
        tags: org.interests || [],
        route: { name: 'orgs-id', params: { id: org.slug || org.id } }
    }
}

const processUserData = (user: any, forceType?: 'mentor' | 'mentee' | 'volunteer' | 'user') => {
    const attributes: string[] = []
    const attributeIcons: string[] = []

    // For location - using formatLocation logic or fallback to geoCity
    if (user.geoCity) {
        attributes.push("" + user.geoCity + (user.geoCountry ? ", " + user.geoCountry : ""))
        attributeIcons.push('location-outline')
    }

    // Get current job info - handle both nested (Firestore) and flattened (Typesense) structures
    let jobInfo = ''
    if (user.jobs?.length) {
        const currentJob = orderBy(user.jobs, 'endYear', 'desc')[0]
        jobInfo = [currentJob?.position, currentJob?.org].filter(Boolean).join(' @ ')
    } else if (user.jobsText) {
        // Typesense flattened format - jobsText can be string or array depending on data source
        if (Array.isArray(user.jobsText)) {
            jobInfo = user.jobsText[0] || ''
        } else {
            // String format: take first job (before comma if multiple)
            jobInfo = user.jobsText.split(',')[0]?.trim() || ''
        }
    }

    const toasts: string[] = []
    const toastTypes: string[] = []
    const toastTitles: string[] = []

    // Determine profile type and add appropriate toast (colors match nav section)
    if (forceType === 'volunteer' || (!forceType && (user.volunteerProfile || user.volunteerInterests) && !user.mentorProfile && !user.menteeProfile)) {
        toasts.push('DOBROVOĽNÍK')
        toastTypes.push('yellow')
        toastTitles.push('')
    } else if (forceType === 'mentor' || (!forceType && (user.mentorProfile || user.mentorInterests))) {
        toasts.push('MENTOR')
        toastTypes.push('teal')
        toastTitles.push('')
    } else if (forceType === 'mentee' || user.menteeProfile) {
        toasts.push('MENTEE')
        toastTypes.push('teal')
        toastTitles.push('')
    }

    // Users with an active mentoring (same wording and tooltip as the mentor profile and MentorRow)
    if (forceType === 'mentor' && user.mentorBooked) {
        toasts.push('Obsadené kapacity')
        toastTypes.push('orange')
        toastTitles.push('Mentor má aktívny mentoring')
    }
    if (forceType === 'mentee' && user.menteeBooked) {
        toasts.push('Obsadené kapacity')
        toastTypes.push('orange')
        toastTitles.push('Mentee má aktívny mentoring')
    }

    // Determine tags based on context - handle both nested and flattened structures
    let tags: string[] = []
    if (forceType === 'volunteer') {
        // Typesense: volunteerInterests, Firestore: volunteerProfile.interests
        tags = user.volunteerInterests?.slice(0, 5) ?? user.volunteerProfile?.interests?.slice(0, 5) ?? user.interests?.slice(0, 5) ?? []
    } else if (forceType === 'mentee') {
        tags = user.menteeProfile?.lookingFor?.slice(0, 5) ?? user.interests?.slice(0, 5) ?? []
    } else if (forceType === 'mentor') {
        // Typesense: mentorInterests, Firestore: mentorProfile.interests
        tags = user.mentorInterests?.slice(0, 5) ?? user.mentorProfile?.interests?.slice(0, 5) ?? user.interests?.slice(0, 5) ?? []
    } else {
        tags = user.interests?.slice(0, 5) ?? []
    }

    // Use slug for SEO-friendly URLs, fallback to ID. When both exist, pass the
    // doc id as ?uid= so the server can disambiguate if slugs ever collide again.
    const slugOrId = user.slug || user.id
    const query = user.slug && user.id ? { uid: user.id } : undefined

    // Determine route based on forceType or available profile types
    let route
    if (forceType === 'volunteer') {
        route = { name: 'dobrovolnictvo-dobrovolnici-id', params: { id: slugOrId }, query }
    } else if (forceType === 'mentee') {
        route = { name: 'mentees-slug', params: { slug: slugOrId }, query }
    } else if (forceType === 'mentor') {
        route = { name: 'mentor-id', params: { id: slugOrId }, query }
    } else if (user.mentorProfile || user.mentorInterests) {
        route = { name: 'mentor-id', params: { id: slugOrId }, query }
    } else if (user.menteeProfile) {
        route = { name: 'mentees-slug', params: { slug: slugOrId }, query }
    } else if (user.volunteerProfile || user.volunteerInterests) {
        route = { name: 'dobrovolnictvo-dobrovolnici-id', params: { id: slugOrId }, query }
    } else {
        route = { name: 'u-id', params: { id: slugOrId }, query }
    }

    // Default image for user profiles. A few profiles store the photo itself as a base64 data: URL
    // (0.5 MB+ each), which would be inlined into every listing page's HTML; show the placeholder instead.
    const defaultImage = defaultImageUrl
    const profileImg = user.profileImg?.startsWith('data:') ? '' : user.profileImg

    // Strip HTML tags from description for clean display
    // Check shortProfile first (both Firestore and Typesense have this)
    let rawDescription = user.shortProfile || ''
    // For mentees, show their goals or current situation if no shortProfile
    if (forceType === 'mentee' && !rawDescription) {
        rawDescription = user.menteeProfile?.goals || ''
    }
    // For volunteers, check nested Firestore fields as fallback
    if (forceType === 'volunteer' && !rawDescription) {
        rawDescription = user.volunteerProfile?.helpHow || user.volunteerProfile?.fieldOfExperienceDetails || ''
    }
    const cleanDescription = rawDescription.replace(/<[^>]*>/g, '').trim()

    return {
        imageLink: profileImg || defaultImage,
        title: getFullName(user),
        subscript: jobInfo || '',
        description: cleanDescription,
        toasts,
        toastTypes,
        toastTitles,
        attributes: attributes.filter(Boolean),
        attributeIcons: attributeIcons.filter(Boolean),
        tags,
        route
    }
}

// Determine the forceType for user processing based on props.type
const userForceType = computed(() => {
    if (props.type === 'mentor') return 'mentor' as const
    if (props.type === 'mentee') return 'mentee' as const
    if (props.type === 'volunteer') return 'volunteer' as const
    return undefined
})

// Computed properties that prioritize typed data over legacy props
const computedImageLink = computed(() => {
    if (props.data) {
        // Respect explicit type prop first
        if (props.type === 'org') return processOrgData(props.data).imageLink
        if (props.type === 'job') return processJobData(props.data as Job).imageLink
        if (props.type === 'project') return processProjectData(props.data as Project).imageLink
        // Fall back to type guards
        if (isJob(props.data)) return processJobData(props.data).imageLink
        if (isProject(props.data)) return processProjectData(props.data).imageLink
        if (isOrg(props.data)) return processOrgData(props.data).imageLink
        if (isUser(props.data)) return processUserData(props.data, userForceType.value).imageLink
    }
    return props.imageLink || ''
})

const computedImageFit = computed(() => props.imageFit)

const computedTitle = computed(() => {
    if (props.data) {
        // Respect explicit type prop first
        if (props.type === 'org') return processOrgData(props.data).title
        if (props.type === 'job') return processJobData(props.data as Job).title
        if (props.type === 'project') return processProjectData(props.data as Project).title
        // Fall back to type guards
        if (isJob(props.data)) return processJobData(props.data).title
        if (isProject(props.data)) return processProjectData(props.data).title
        if (isOrg(props.data)) return processOrgData(props.data).title
        if (isUser(props.data)) return processUserData(props.data, userForceType.value).title
    }
    return props.title || ''
})

const computedSubscript = computed(() => {
    if (props.data) {
        // Respect explicit type prop first
        if (props.type === 'org') return processOrgData(props.data).subscript
        if (props.type === 'job') return processJobData(props.data as Job).subscript
        if (props.type === 'project') return processProjectData(props.data as Project).subscript
        // Fall back to type guards
        if (isJob(props.data)) return processJobData(props.data).subscript
        if (isProject(props.data)) return processProjectData(props.data).subscript
        if (isOrg(props.data)) return processOrgData(props.data).subscript
        if (isUser(props.data)) return processUserData(props.data, userForceType.value).subscript
    }
    return props.subscript || ''
})

// Determine if we should show the subscript icon (hide for projects and orgs)
const showSubscriptIcon = computed(() => {
    if (props.data) {
        if (isProject(props.data)) return false
        if (isOrg(props.data)) return false
    }
    if (props.type === 'project') return false
    return true
})

const computedToasts = computed(() => {
    if (props.data) {
        // Respect explicit type prop first
        if (props.type === 'org') return processOrgData(props.data).toasts
        if (props.type === 'job') return processJobData(props.data as Job).toasts
        if (props.type === 'project') return processProjectData(props.data as Project).toasts
        // Fall back to type guards
        if (isJob(props.data)) return processJobData(props.data).toasts
        if (isProject(props.data)) return processProjectData(props.data).toasts
        if (isOrg(props.data)) return processOrgData(props.data).toasts
        if (isUser(props.data)) return processUserData(props.data, userForceType.value).toasts
    }
    return props.toasts || []
})

const computedToastTypes = computed(() => {
    if (props.data) {
        if (props.type === 'org') return processOrgData(props.data).toastTypes
        if (props.type === 'job') return processJobData(props.data as Job).toastTypes
        if (props.type === 'project') return processProjectData(props.data as Project).toastTypes
        if (isJob(props.data)) return processJobData(props.data).toastTypes
        if (isProject(props.data)) return processProjectData(props.data).toastTypes
        if (isOrg(props.data)) return processOrgData(props.data).toastTypes
        if (isUser(props.data)) return processUserData(props.data, userForceType.value).toastTypes
    }
    return props.toastType || []
})

const computedToastTitles = computed(() => {
    if (props.data) {
        if (props.type === 'job') return processJobData(props.data as Job).toastTitles
        if (props.type === 'org' || props.type === 'project') return []
        if (isJob(props.data)) return processJobData(props.data).toastTitles
        if (isProject(props.data) || isOrg(props.data)) return []
        if (isUser(props.data)) return processUserData(props.data, userForceType.value).toastTitles
    }
    return props.toastTitles || []
})

const computedAttributes = computed(() => {
    if (props.data) {
        if (props.type === 'org') return processOrgData(props.data).attributes
        if (props.type === 'job') return processJobData(props.data as Job).attributes
        if (props.type === 'project') return processProjectData(props.data as Project).attributes
        if (isJob(props.data)) return processJobData(props.data).attributes
        if (isProject(props.data)) return processProjectData(props.data).attributes
        if (isOrg(props.data)) return processOrgData(props.data).attributes
        if (isUser(props.data)) return processUserData(props.data, userForceType.value).attributes
    }
    return props.attributes || []
})

const computedAttributeIcons = computed(() => {
    if (props.data) {
        if (props.type === 'org') return processOrgData(props.data).attributeIcons
        if (props.type === 'job') return processJobData(props.data as Job).attributeIcons
        if (props.type === 'project') return processProjectData(props.data as Project).attributeIcons
        if (isJob(props.data)) return processJobData(props.data).attributeIcons
        if (isProject(props.data)) return processProjectData(props.data).attributeIcons
        if (isOrg(props.data)) return processOrgData(props.data).attributeIcons
        if (isUser(props.data)) return processUserData(props.data, userForceType.value).attributeIcons
    }
    return props.attributeIconNames || []
})

const computedTags = computed(() => {
    if (props.data) {
        if (props.type === 'org') return processOrgData(props.data).tags
        if (props.type === 'job') return processJobData(props.data as Job).tags
        if (props.type === 'project') return processProjectData(props.data as Project).tags
        if (isJob(props.data)) return processJobData(props.data).tags
        if (isProject(props.data)) return processProjectData(props.data).tags
        if (isOrg(props.data)) return processOrgData(props.data).tags
        if (isUser(props.data)) return processUserData(props.data, userForceType.value).tags
    }
    return props.tags || []
})

const computedDescription = computed(() => {
    if (props.data && isUser(props.data)) {
        return processUserData(props.data, userForceType.value).description
    }
    return props.description || ''
})

const computedRoute = computed(() => {
    if (props.to) return props.to
    if (props.data) {
        if (props.type === 'org') return processOrgData(props.data).route
        if (props.type === 'job') return processJobData(props.data as Job).route
        if (props.type === 'project') return processProjectData(props.data as Project).route
        if (isJob(props.data)) return processJobData(props.data).route
        if (isProject(props.data)) return processProjectData(props.data).route
        if (isOrg(props.data)) return processOrgData(props.data).route
        if (isUser(props.data)) return processUserData(props.data, userForceType.value).route
    }
    return { name: props.page || '', params: props.pageParams || {} }
})

function handleTagClick(tag: string) {
    emit('tag-click', tag)
}

function handleCardClick(event: MouseEvent) {
    // Don't emit card click if the click was on a tag
    if ((event.target as HTMLElement).closest('.tagArea')) {
        return
    }
    emit('click', event)
}

</script>

<style scoped>

.CMasonryCard {
    border-radius: 8px;
    background-color: white;
    width: 100%;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    display: inline-flex;
    flex-direction: column;
    overflow: hidden;
    text-decoration: none;
    color: inherit;
    transition: box-shadow 0.2s ease-in-out;
}

.CMasonryCard:hover {
    box-shadow:
        0 10px 26px 0 rgba(31, 31, 35, 0.1),
        0 30px 26px 0 rgba(31, 31, 35, 0.1);
}

.image-container {
    position: relative;
    width: 100%;
    aspect-ratio: 1/0.8;
    overflow: hidden;
}

.image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    transition: transform 0.3s ease;
}

.image--contain {
    object-fit: contain;
    padding: 1rem;
}

.CMasonryCard:hover .image {
    transform: scale(1.05);
}

.toasts {
    position: absolute;
    bottom: 0.5rem;
    left: 0.5rem;
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
    pointer-events: auto;
}

/* Badges shown at the top of the body when the card has no image. */
.toasts-inline {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin: 1rem 1rem 0;
}

/* Logo-only image on phones: a short band with the badges on the left and the logo on the right,
   instead of a 1 / 0.8 box that is mostly empty space around a small logo. */
@media (max-width: 639px) {
    .CMasonryCard--compact-image .image-container {
        aspect-ratio: auto;
        height: 5.5rem;
        /* The band is wider than the logo, so it carries the placeholder color of the <img> */
        background-color: var(--color-gray-300);
    }

    [data-theme="dark"] .CMasonryCard--compact-image .image-container {
        background-color: var(--color-bg-subtle);
    }

    .CMasonryCard--compact-image .image {
        width: 45%;
        margin-left: auto;
        object-position: right center;
        padding: 0.75rem 1rem;
    }

    .CMasonryCard--compact-image .toasts {
        flex-wrap: wrap;
        max-width: 55%;
    }
}

/* Compact, image-less card: tighten the top so the title leads. */
.CMasonryCard--no-image .title {
    margin-top: 0.75rem;
}

.CMasonryCard--no-image .toasts-inline + .title {
    margin-top: 0.75rem;
}

.title {
    color: #0d0e2d;
    font-size: 18px;
    font-weight: 500;
    letter-spacing: 0;
    line-height: 22px;
    margin-left: 1rem;
    margin-right: 1rem;
    margin-top: 1rem;
    margin-bottom: 0;
}

.subscript {
    display: inline-block;
    width: 100%;
    color: #9fa0ac;
    font-size: 13px;
    letter-spacing: 0;
    line-height: 18px;
    margin-left: 1rem;
    margin-right: 1rem;
    margin-top: 0.375rem;
}

.subscript-with-icon {
    display: flex;
    align-items: flex-start;
    gap: 0.375rem;
    color: #6c5d73;
    font-size: 13px;
    line-height: 1.4;
    margin: 0.375rem 1rem 0;
}

.description {
    color: #6c5d73;
    font-size: 14px;
    line-height: 1.4;
    margin: 0.5rem 1rem 0;
    display: -webkit-box;
    -webkit-line-clamp: 5;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
}

.attributes {
    display: flex;
    flex-direction: column;
    font-size: 13px;
    color: #6c5d73;
    line-height: 1.4;
    width: 100%;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    padding-left: 1rem;
    padding-right: 1rem;
}

.attribute {
    display: flex;
    align-items: flex-start;
    gap: 0.375rem;
    margin-bottom: 0.25rem;
}

.tagArea {
    margin-left: 1rem;
    margin-right: 1rem;
    margin-bottom: 1rem;
    margin-top: 0.5rem;
}


/* Neutral surfaces follow the selected theme; brand colors and images retain their colors. */
[data-theme="dark"] .CMasonryCard {
    background-color: var(--color-surface-card)
}
[data-theme="dark"] .title {
    color: var(--color-txt-headings);
}
[data-theme="dark"] .description,
[data-theme="dark"] .subscript,
[data-theme="dark"] .subscript-with-icon,
[data-theme="dark"] .attributes {
    color: var(--color-txt-body);
}
</style>
